<template>
  <div class="form-field">
    <label v-if="label" :for="id" class="form-label">{{ label }}</label>
    <div class="relative">
      <!-- 前置图标 -->
      <div v-if="prefixIcon" class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
        <i :class="[prefixIcon, 'text-gray-400']"></i>
      </div>
      <!-- 前置文本 -->
      <div v-else-if="prefix" class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
        <span class="text-gray-500 sm:text-sm">{{ prefix }}</span>
      </div>
      
      <!-- 输入框 -->
      <input
        :id="id"
        :ref="id"
        :type="type"
        :name="name || id"
        :value="modelValue"
        @input="$emit('update:modelValue', $event.target.value)"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :min="min"
        :max="max"
        :step="step"
        :class="[
          'form-input',
          (prefixIcon || prefix) ? 'pl-10' : 'px-4',
          suffixIcon || clearable || loading ? 'pr-10' : 'px-4',
          disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '',
          error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : '',
          inputClass
        ]"
        v-bind="$attrs"
      />
      
      <!-- 后置图标 -->
      <div v-if="suffixIcon" class="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none">
        <i :class="[suffixIcon, 'text-gray-400']"></i>
      </div>
      
      <!-- 加载状态 -->
      <div v-if="loading" class="absolute inset-y-0 right-0 pr-3.5 flex items-center">
        <div class="animate-spin h-4 w-4 text-blue-500">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
      
      <!-- 清除按钮 -->
      <div v-else-if="clearable && modelValue" class="absolute inset-y-0 right-0 pr-3.5 flex items-center">
        <button 
          type="button" 
          @click="$emit('update:modelValue', '')" 
          class="text-gray-400 hover:text-gray-500 transition-colors duration-150"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
    
    <!-- 帮助文本 -->
    <div v-if="helpText" class="mt-1.5 text-xs text-gray-500">{{ helpText }}</div>
    
    <!-- 错误信息 -->
    <div v-if="error" class="mt-1.5 text-xs text-red-500 flex items-center">
      <i class="fas fa-exclamation-circle mr-1"></i>
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';

defineProps({
  // 输入框标识
  id: {
    type: String,
    required: true
  },
  // 标签文本
  label: {
    type: String,
    default: ''
  },
  // 输入框名称
  name: {
    type: String,
    default: ''
  },
  // 绑定值
  modelValue: {
    type: [String, Number],
    default: ''
  },
  // 输入框类型
  type: {
    type: String,
    default: 'text'
  },
  // 占位文本
  placeholder: {
    type: String,
    default: ''
  },
  // 前缀图标
  prefixIcon: {
    type: String,
    default: ''
  },
  // 前缀文本
  prefix: {
    type: String,
    default: ''
  },
  // 后缀图标
  suffixIcon: {
    type: String,
    default: ''
  },
  // 加载状态
  loading: {
    type: Boolean,
    default: false
  },
  // 禁用状态
  disabled: {
    type: Boolean,
    default: false
  },
  // 只读状态
  readonly: {
    type: Boolean,
    default: false
  },
  // 是否可清除
  clearable: {
    type: Boolean,
    default: false
  },
  // 最小值（适用于数字输入）
  min: {
    type: [Number, String],
    default: undefined
  },
  // 最大值（适用于数字输入）
  max: {
    type: [Number, String],
    default: undefined
  },
  // 步长（适用于数字输入）
  step: {
    type: [Number, String],
    default: undefined
  },
  // 帮助文本
  helpText: {
    type: String,
    default: ''
  },
  // 错误信息
  error: {
    type: String,
    default: ''
  },
  // 自定义输入框类名
  inputClass: {
    type: String,
    default: ''
  }
});

defineEmits(['update:modelValue']);
</script>

<style scoped>
.form-field {
  @apply mb-4;
}

.form-label {
  @apply block text-sm font-medium text-gray-700 mb-1.5;
}

.form-input {
  @apply block w-full rounded-md border-gray-300 shadow-sm 
  text-gray-700 placeholder-gray-400 sm:text-sm py-2.5
  transition-all duration-200 ease-in-out
  focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500;
}

/* 禁用状态 */
.form-input:disabled {
  @apply opacity-70 cursor-not-allowed;
}

/* 悬停效果 */
.form-input:not(:disabled):hover {
  @apply border-gray-400;
}

/* 错误状态 */
.form-input.error {
  @apply border-red-300 pr-10;
}
.form-input.error:focus {
  @apply ring-red-500/30 border-red-500;
}
</style> 